import { ChangeEvent, FocusEvent } from 'react';
import { FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { Textarea, TextareaProps } from '@givebutter/primitives/textarea';
import { useFormError } from 'components/form/UseFormError';

interface UseFormTextAreaProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K>,
        Omit<TextareaProps, 'name' | 'defaultValue'> {}
const UseFormTextArea = <T extends FieldValues, K extends FieldPath<T>>(
    props: UseFormTextAreaProps<T, K>,
) => {
    const {
        defaultValue,
        control,
        name: nameExternal,
        onChange: onChangeExternal,
        onBlur: onBlurExternal,
        rules,
        children,
        disabled,
        ...restTextAreaProps
    } = props;

    const {
        field: {
            value,
            name,
            onChange: onChangeInternal,
            onBlur: onBlurInternal,
            ...restUseFormProps
        },
    } = useController({
        name: nameExternal,
        control,
        rules,
        defaultValue,
        disabled,
    });

    const error = useFormError(name as FieldPath<T>);

    const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        onChangeInternal(e.target.value);
        onChangeExternal?.(e);
    };

    const onBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
        onChange(e);
        onBlurInternal();
        onBlurExternal?.(e);
    };

    return (
        <Textarea
            error={error?.message}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            {...restTextAreaProps}
            {...restUseFormProps}
        />
    );
};

export { UseFormTextArea };
